import React, { useEffect, useState } from 'react';
import { Check, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { AdminAPI, RateCardRow } from '../lib/api';

/**
 * The shared pieces of the credit screens (28 Sep, mig 0702).
 *
 * One balance per organisation, one rate card: the global card
 * (credit_pricing_defaults) and an organisation's own number for any row
 * (credit_pricing_overrides, agent_id NULL). Agent-level prices, per-agent
 * grants, money rates, plans and packs are retired. The Rate card page and the
 * organisation's "Credits & terms" card both use these, so an override set in
 * one place reads the same in the other.
 */

export const reasonOf = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** The API's family names — and the older ones, read the same way. */
const FAMILY_ALIAS: Record<string, string> = {
  outcomes: 'success', messaging: 'messages', agents: 'conversations', build: 'building',
};
export const familyOf = (f?: string | null) => FAMILY_ALIAS[f || 'other'] ?? (f || 'other');

export const FAMILY_ORDER: { family: string; label: string }[] = [
  { family: 'leads', label: 'Leads' },
  { family: 'success', label: 'Success fees' },
  { family: 'messages', label: 'Messages' },
  { family: 'calls', label: 'Calls' },
  { family: 'conversations', label: 'Conversations' },
  { family: 'building', label: 'Building' },
  { family: 'other', label: 'Other' },
];

/** Rows grouped in the card's order; unknown families land in "Other". */
export function groupByFamily<T extends { family?: string | null }>(rows: T[]): { family: string; label: string; rows: T[] }[] {
  const known = new Set(FAMILY_ORDER.map(f => f.family));
  const fam = (r: T) => (known.has(familyOf(r.family)) ? familyOf(r.family) : 'other');
  return FAMILY_ORDER
    .map(f => ({ ...f, rows: rows.filter(r => fam(r) === f.family) }))
    .filter(g => g.rows.length > 0);
}

export const Banner: React.FC<{ kind: 'error' | 'info' | 'success'; children: React.ReactNode }> = ({ kind, children }) => (
  <div className={`px-4 py-3 rounded text-sm border ${
    kind === 'error'
      ? 'bg-red-50 border-red-200 text-red-700'
      : kind === 'success'
        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
        : 'bg-blue-50 border-blue-200 text-blue-700'
  }`}>
    {children}
  </div>
);

export const NotChargedTag: React.FC = () => (
  <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
    not charged
  </span>
);

export const AgreedTag: React.FC = () => (
  <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 border border-blue-200">
    Agreed for this org
  </span>
);

// ---------------------------------------------------------------------------
// An organisation's effective rate card, with its own numbers editable
// ---------------------------------------------------------------------------

export const OrgRateCardTable: React.FC<{
  api: AdminAPI;
  orgId: string;
  rows: RateCardRow[];
  onChange: (rows: RateCardRow[]) => void;
  /** Hide the leads + success rows (the terms editor owns them). */
  hideTermsRows?: boolean;
}> = ({ api, orgId, rows, onChange, hideTermsRows }) => {
  const visible = hideTermsRows
    ? rows.filter(r => !['leads', 'success'].includes(familyOf(r.family)))
    : rows;
  const groups = groupByFamily(visible);
  if (groups.length === 0) return <p className="px-6 py-4 text-sm text-gray-500">No prices on the card yet.</p>;
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Thing</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Per</th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Default</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">This organisation</th>
          <th className="px-4 py-2" />
        </tr>
      </thead>
      {groups.map(g => (
        <tbody key={g.family} className="bg-white divide-y divide-gray-100">
          <tr className="bg-gray-50/60">
            <th colSpan={5} className="px-4 py-1.5 text-left text-xs font-semibold text-gray-700">{g.label}</th>
          </tr>
          {g.rows.map(r => (
            <OrgRateRow
              key={r.action}
              row={r}
              onSave={async (credits) => onChange(await api.setOrgOverride(orgId, r.action, credits))}
              onClear={async () => onChange(await api.clearOrgOverride(orgId, r.action))}
            />
          ))}
        </tbody>
      ))}
    </table>
  );
};

const OrgRateRow: React.FC<{
  row: RateCardRow;
  onSave: (credits: number) => Promise<void>;
  onClear: () => Promise<void>;
}> = ({ row, onSave, onClear }) => {
  const agreed = row.resolved_from === 'org_override';
  const [val, setVal] = useState(agreed ? String(row.credits) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setVal(agreed ? String(row.credits) : ''); }, [agreed, row.credits]);
  const num = val === '' ? null : Number(val);
  const valid = num !== null && Number.isFinite(num) && num >= 0 && Number.isInteger(num);
  const dirty = valid && (!agreed || num !== row.credits);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(reasonOf(e)); } finally { setBusy(false); }
  };

  return (
    <tr>
      <td className="px-4 py-2 text-sm text-gray-900">
        {row.label}
        {row.customer_visible === false && <NotChargedTag />}
      </td>
      <td className="px-4 py-2 text-xs text-gray-500">{row.unit}</td>
      <td className="px-4 py-2 text-right text-sm tabular-nums text-gray-500">{row.default_credits ?? '—'}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <input
            type="number" min={0} step={1} value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && dirty) run(() => onSave(num!)); }}
            placeholder={String(row.default_credits ?? 0)}
            aria-label={`${row.label}: this organisation's credits`}
            className="w-24 rounded border-gray-300 text-sm tabular-nums focus:border-blue-500 focus:ring-blue-500"
          />
          {agreed ? <AgreedTag /> : <span className="text-[11px] text-gray-400">Default</span>}
        </div>
        {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap space-x-1">
        {dirty && (
          <button
            disabled={busy}
            onClick={() => run(() => onSave(num!))}
            className="px-2.5 py-1 text-xs rounded font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}Set
          </button>
        )}
        {agreed && (
          <button
            disabled={busy}
            onClick={() => {
              if (!window.confirm(`Clear this organisation's own number for "${row.label}"? It goes back to the default (${row.default_credits ?? 0}).`)) return;
              run(onClear);
            }}
            className="px-2.5 py-1 text-xs rounded font-medium text-red-600 hover:bg-red-50 inline-flex items-center disabled:opacity-50"
          >
            <Trash2 className="w-3 h-3 mr-1" />Clear
          </button>
        )}
        {!agreed && val !== '' && (
          <button onClick={() => setVal('')} className="text-xs text-gray-400 hover:text-gray-600" title="Reset">
            <RotateCcw className="w-3 h-3" />
          </button>
        )}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Grants
// ---------------------------------------------------------------------------

export const GrantCreditsForm: React.FC<{
  api: AdminAPI;
  orgId: string;
  orgName?: string;
  onDone: (message: string) => void;
}> = ({ api, orgId, orgName, onDone }) => {
  const [credits, setCredits] = useState('');
  const [note, setNote] = useState('');
  const [days, setDays] = useState('365');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const c = Number(credits);
  const d = Number(days);
  const valid = Number.isInteger(c) && c > 0 && Number.isInteger(d) && d > 0 && d <= 3650;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await api.grantCredits(orgId, c, note.trim() || undefined, d);
      setCredits(''); setNote('');
      onDone(`Added ${c.toLocaleString()} credits${orgName ? ` to ${orgName}` : ''}. They last ${d.toLocaleString()} days.`);
    } catch (e) { setErr(reasonOf(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-xs text-gray-600">Credits
          <input type="number" min={1} value={credits} onChange={e => setCredits(e.target.value)}
            className="mt-1 block w-32 rounded border-gray-300 text-sm" placeholder="e.g. 5000" />
        </label>
        <label className="block text-xs text-gray-600">Lasts (days)
          <input type="number" min={1} max={3650} value={days} onChange={e => setDays(e.target.value)}
            className="mt-1 block w-24 rounded border-gray-300 text-sm" />
        </label>
        <label className="block flex-1 min-w-[12rem] text-xs text-gray-600">Note (optional)
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            className="mt-1 block w-full rounded border-gray-300 text-sm" placeholder="Why these credits" />
        </label>
        <button onClick={submit} disabled={!valid || busy}
          className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}Add credits
        </button>
      </div>
      <p className="text-xs text-gray-500">Credits go to the organisation's one balance; every agent draws from it.</p>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
};

export const LeadBatchForm: React.FC<{
  api: AdminAPI;
  orgId: string;
  orgName?: string;
  onDone: (message: string) => void;
}> = ({ api, orgId, orgName, onDone }) => {
  const [leads, setLeads] = useState('');
  const [perLead, setPerLead] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const l = Number(leads);
  const p = Number(perLead);
  const valid = Number.isInteger(l) && l > 0 && Number.isInteger(p) && p > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.grantForLeadBatch(orgId, { leads_count: l, credits_per_lead: p, note: note.trim() || undefined });
      setLeads(''); setPerLead(''); setNote('');
      onDone(`Added ${r.credits_granted.toLocaleString()} credits for ${r.leads_count.toLocaleString()} leads (${r.credits_per_lead.toLocaleString()} each)${orgName ? ` to ${orgName}` : ''}.`);
    } catch (e) { setErr(reasonOf(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-xs text-gray-600">Leads in the batch
          <input type="number" min={1} value={leads} onChange={e => setLeads(e.target.value)}
            className="mt-1 block w-32 rounded border-gray-300 text-sm" placeholder="e.g. 2500" />
        </label>
        <label className="block text-xs text-gray-600">Credits per lead
          <input type="number" min={1} value={perLead} onChange={e => setPerLead(e.target.value)}
            className="mt-1 block w-32 rounded border-gray-300 text-sm" placeholder="e.g. 5" />
        </label>
        <label className="block flex-1 min-w-[12rem] text-xs text-gray-600">Batch (list name, date)
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            className="mt-1 block w-full rounded border-gray-300 text-sm" />
        </label>
        <button onClick={submit} disabled={!valid || busy}
          className="px-4 py-2 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {valid ? `Add ${(l * p).toLocaleString()} credits` : 'Add credits'}
        </button>
      </div>
      <p className="text-xs text-gray-500">
        A grant for a batch: leads × credits per lead, typed for this batch. This is not what a lead costs; that is the "Per lead" price on the terms.
      </p>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
};
